/**
 * WAF CRS (Core Rule Set) managed rules (Owl/Mlytics) — per-domain typed rule
 * listing + bulk enable/action mutation. Service-scoped (path `/services/:service_id/waf/…`)
 * and domain-anchored (`?domain=<fqdn>`); the provider resolves the customer (tier-2
 * impersonation) from the service's zone.
 *
 * Backed by `modules/service/waf/controller_rules.go`:
 *   - `GET   /services/:service_id/waf/rules/options` → static filter tokens (categories, actions)
 *   - `GET   /services/:service_id/waf/rules`         → a domain's rules (filter by category/action/enabled/rule_id)
 *   - `PATCH /services/:service_id/waf/rules`         → bulk change action and/or enabled over `rule_ids`
 *
 * The listing is NOT paginated (full rule set). The bulk PATCH is the only
 * writer — a single-row change is just a PATCH with a one-element `rule_ids`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/** Canonical rule category (the 3 tabs). */
export type WafRuleCategory = 'xss' | 'sql_injection' | 'generic_injection';
/** Canonical rule action. */
export type WafRuleAction = 'block' | 'simulate' | 'pass';

/** One CRS rule (mirrors BE `providers.WAFRule`). */
export interface WafRule {
  id: number;
  action: WafRuleAction;
  enabled: boolean;
  /** Human-readable rule description. */
  message: string;
  category: WafRuleCategory;
}

/** One filter dropdown option (mirrors BE `providers.WAFFilterOption`). */
export interface WafRuleOption {
  value: string;
  label: string;
}

/** Filter tokens (mirrors BE `providers.WAFRuleFilterOptionsResponse`). */
export interface WafRuleOptions {
  categories: WafRuleOption[];
  actions: WafRuleOption[];
}

/** Server-side rule-list filter (combined AND). */
export interface WafRuleFilter {
  category?: WafRuleCategory;
  action?: WafRuleAction | '';
  enabled?: boolean;
  rule_id?: number;
}

/** Bulk mutation body (mirrors BE `updateRulesRequest`). Omitted field = unchanged. */
export interface WafRuleUpdate {
  rule_ids: number[];
  action?: WafRuleAction;
  enabled?: boolean;
}

interface WafRulesResponse {
  domain: string;
  rules: WafRule[];
}

/** Canonical category/action filter tokens (static metadata). */
export function useWafRuleOptions(serviceId: string | undefined) {
  return useQuery<WafRuleOptions>({
    queryKey: wafKeys.ruleOptions(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 60 * 60_000,
    queryFn: async () =>
      unwrap<WafRuleOptions>(await serviceApiV3.get(`/services/${serviceId}/waf/rules/options`)),
  });
}

/** List a domain's CRS rules (full set; optional category/action/enabled filter). */
export function useWafRules(
  serviceId: string | undefined,
  domain: string | undefined,
  filter: WafRuleFilter = {},
) {
  const params = { domain, ...filter };
  return useQuery<WafRule[]>({
    queryKey: wafKeys.rules(serviceId ?? '', domain ?? '', filter as Record<string, unknown>),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<WafRulesResponse>(await serviceApiV3.get(`/services/${serviceId}/waf/rules`, { params })).rules ?? [],
  });
}

/** Bulk-update a domain's CRS rules — action and/or enabled (`PATCH /waf/rules`). */
export function useUpdateWafRules(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; input: WafRuleUpdate }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: WafRuleUpdate }) =>
      unwrap<unknown>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/rules`, input, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'rules'] });
      options?.onSuccess?.(...args);
    },
  });
}
